export enum NeoFeatureFlag {
  NEO_DASHBOARDS_ENABLED = 'neo_dashboards_enabled',
  /** Gates the Agents surface (list + detail) in the neo-platform app. */
  NEO_AGENTS_ENABLED = 'neo_agents_enabled',
  /**
   * Gates the unified Agents experience: list + detail render off the single
   * `/neo/agents` contract (scheduled jobs + workflows, later Trinity) instead
   * of the job-only `/neo/jobs` screens. Additive — the legacy screens render
   * when this is off. Requires the matrix `neo_agents_api_enabled` gate so the
   * endpoint exists; until it's deployed the hooks fall back to composing the
   * legacy job/workflow endpoints client-side.
   */
  NEO_AGENTS_UNIFIED = 'neo_agents_unified',
  /** Gates Apple login on Neo until a production Apple OAuth URL is configured. */
  NEO_APPLE_LOGIN_ENABLED = 'neo_apple_login_enabled',
  /** Pilot owners — gates who can create a team workspace, until billing exists. */
  NEO_PILOT_OWNERS = 'neo_pilot_owners',
  /** ShipHero email/password connect; when off, legacy token paste form. */
  NEO_SHIPHERO_OAUTH_ENABLED = 'neo_shiphero_oauth_enabled',
}

export enum CompanyFeatureFlag {
  SVB_SUB_ACCOUNT = 'svb_sub_account',
  INVOICE_STATEMENTS = 'invoice_statements',
  SHOW_CANCELED_SCHEDULED_WORKERS = 'show_canceled_scheduled_workers',
  INVOICE_ONLINE_PAYMENT_LINK = 'invoice_online_payment_link',
  BIZ_APP_SHOW_BILLING_VERIFICATION_PHONE = 'biz_app_show_billing_verification_phone',
  BIZ_APP_VOICE_TO_TEXT = 'biz_app_voice_to_text',
  /** Aligns with Ops gate key — block policy wizard for company-initiated blocks. */
  BLOCK_CONNECTION_POLICY_REASONS = 'block_connection_policy_reasons',
  CONCURRENT_SAME_COMPANY_INVITES = 'concurrent_same_company_invites',
}

export enum OpsFeatureFlag {
  WORKERS_COMP_ADMIN = 'ops_console_workers_comp_admin',
  OPS_TASKS = 'ops_tasks',
  WORKER_PERFORMANCE = 'worker_performance',
  SHIFT_AUTO_FILL_ACTIVE = 'shift_auto_fill_active',
  SHIFT_AUTO_FILL_ON_DEMAND = 'shift_auto_fill_on_demand',
  INDEED_JOB_POSTS_OPS_CONSOLE = 'indeed_job_posts_ops_console',
  INDEED_W2_JOB_POSTS_OPS_CONSOLE = 'indeed_job_posts_ops_console_for_w2',
  INDEED_FREE_POST_JOB_POSTS_OPS_CONSOLE = 'indeed_free_post_job_posts_ops_console',
  APPLICATION_TEMPLATES = 'application_templates',
  MANAGE_COMPANY_REQUIREMENTS = 'manage_company_requirements',
  DOWNLOAD_BANK_PDF = 'download_bank_pdf',
  VETTING_CONTEXT_FORM_CHANGE_DETECTION = 'vetting_context_form_change_detection',
  ENABLE_DEFAULT_VETTING_QUESTIONS = 'enable_default_vetting_questions',
  EDIT_DEFAULT_VETTING_QUESTIONS = 'edit_default_vetting_questions',
  GHOST_PROFILE_INVITATIONS_OPS = 'ghost_profile_invitations_ops',
  ATS_CSV_UPLOAD = 'ats_csv_upload',
  ATS_ADD_BY_PHONE = 'ats_add_by_phone',
  ATS_NOTIFY_WORKERS = 'ats_notify_workers',
  ENABLE_JOB_POST_BIFROST_RAPID_ONBOARDING = 'enable_job_post_bifrost_rapid_onboarding',
  AI_OUTREACH_ADD_TO_SHIFT = 'ai_outreach_add_to_shift',
  SHIFT_COMPLETION_PREDICTOR = 'shift_completion_predictor',
  AI_OUTREACH_GUIDE_WORKER_TO_APPLY = 'ai_outreach_guide_worker_to_apply',
  SENTINEL_BROWSER_NOTIFICATIONS = 'sentinel_browser_notifications',
  AVA_NEW_YEAR_FIREWORKS = 'ava_new_year_fireworks',
  ENABLE_AI_SMS_STEP_IN_WORKFLOW = 'enable_ai_sms_step_in_workflow',
  WORKER_OPS_ASSIGNMENT_FILTER = 'worker_ops_assignment_filter',
  WORKER_OPS_POD_FILTER = 'worker_ops_pod_filter',
  CUSTOMER_OPS_ASSIGNMENT_FILTER = 'customer_ops_assignment_filter',
  INVOICE_ADMINS = 'invoice_admins',
  LIMITS_VOID = 'limits_void',
  NON_INVOICE_ADMINS_WITH_EXTENDED_BILLING_PERMS = 'non_invoice_admins_with_extended_billing_perms',
  BILLING_ADJUSTMENTS_ONLY_INVOICE_UI = 'billing_adjustments_only_invoice_ui',
  SEPARATE_CREDIT_NOTE_STATEMENTS = 'separate_credit_note_statements',
  EXPOSE_MCP_AUTH_FRONT_END = 'expose_mcp_auth_front_end',
  ALLOW_INVOICED_SHIFT_EDITS = 'allow_invoiced_shift_edits',
  USES_CAN_MANAGE_ATS_DOCUSIGN = 'uses_can_manage_ats_docusign',
  REGION_HEALTH_MONITOR = 'region_health_monitor',
  ENABLE_REQUIREMENTS_UI = 'enable_requirements_ui',
  /** AI SMS / Worker Check-in hub (OCC). Pair with gating code that falls back to `ENABLE_REQUIREMENTS_UI` until Statsig is fully cut over. */
  OCC_WORKER_CHECKIN_READINESS_HUB = 'occ_worker_checkin_readiness_hub',
  ATTRIBUTE_V3_ADMIN_WRITE_ALLOWLIST = 'attribute_v3_admin_write_allowlist',
  MANAGE_DOCUMENT_TEMPLATES = 'manage_ops_console_document_templates',
  OPS_VOICE_TO_TEXT = 'ops_voice_to_text',
  OCC_PIPELINE_FUNNEL_TIMINGS = 'occ_pipeline_funnel_timings',
  OP_CALL_VETTING_INGEST_ENABLED = 'op_call_vetting_ingest_enabled',
  NEUTRON_PLATFORM_ENABLED = 'neutron_platform_enabled',
  /** Gates the Neutron Agents tab (overview + agent detail) in ops-console. */
  NEUTRON_AGENTS_ENABLED = 'neutron_agents_enabled',
  /**
   * Internal-only Insights Sandbox page in ops-console: kick off a playground
   * insight run against a chosen operator/org, watch live progress, and view
   * the result. Target @traba.work employees (e.g. ENG/DATA departments) only —
   * the matrix playground endpoints it calls are internal-gated server-side.
   */
  NEUTRON_INSIGHTS_SANDBOX_ENABLED = 'neutron_insights_sandbox_enabled',
  /** Shift draft automation (Neutron save/preview/publish MCP + Slack curation). */
  SHIFT_DRAFTS_ENABLED = 'shift_drafts_enabled',
  BLOCK_CONNECTION_POLICY_REASONS = 'block_connection_policy_reasons',
  CAN_APPROVE_COMPANIES = 'can_approve_companies',
  /** Phase 3 — gates the redesigned W2 ClearingHouse paycheck card and expanded body. */
  CLEARING_HOUSE_W2_UI_OVERHAUL = 'clearing_house_w2_ui_overhaul',
  /**
   * Layered on top of `CLEARING_HOUSE_W2_UI_OVERHAUL`: surfaces the per-shift
   * breakdown by default (no card-level expand) and shows adjustment-aware
   * gross totals so ops aren't misled by the stale Zeal-stored paycheck gross.
   */
  CLEARING_HOUSE_W2_PER_SHIFT = 'clearing_house_w2_per_shift',
  /** Enables the text-selection → Ask Neutron pill in the Ops Console. */
  ASK_NEUTRON_TEXT_SELECTION = 'ask_neutron_text_selection',
}

export enum DynamicConfigs {
  ATS_DYNAMIC_CONFIG = 'ats_dynamic_config',
  OPERATOR_AI_CONFIG = 'operator_ai_config',
  GLOBAL_WORKFLOW_ACCESS = 'global_workflow_access',
  SHIFT_MINIMUM_DURATION_BYPASS = 'shift_minimum_duration_bypass',
  ATTRIBUTE_VETTING_MAP_FOR_AI_CALLING = 'attribute_vetting_map_for_ai_calling',
  FORWARD_FILL_CONFIG = 'forward_fill_config',
  SHIFT_AUTO_FILL_CONFIG = 'shift_auto_fill_config',
  MINIMUM_ROLE_AGE_REQUIREMENT = 'minimum_role_age_requirement',
  ROLE_FAMILY_SCOUT_CONFIG = 'role_family_scout_config',
  COMPANY_HEALTH_DASH = 'company_health_dash',
  FIELD_MONITOR_CONFIG = 'field_monitor_config',
  WORKER_VETTING_CONFIG = 'worker_vetting_config',
  DEFAULT_CUSTOM_VETTING_QUESTIONS = 'default_custom_vetting_questions',
  E_VERIFY_WAIVER_LIMIT = 'e_verify_waiver_limit',
  OVERBOOKING_RESTRICTIONS_CONFIG = 'overbooking_restrictions_config',
  SENTINEL_CONFIG = 'sentinel_config',
  SHIFT_COMPLETION_PREDICTION_CONFIG = 'shift_completion_prediction_config',
  SUGGESTED_TIME_BETWEEN_SHIFTS = 'suggested_time_between_shifts',
  POST_SHIFT_CHECK_IN = 'post_shift_check_in',
  LOCKED_SEARCH_PRESETS = 'locked_search_presets',
  REGION_HEALTH_MONITOR_REGIONAL_STATUS = 'region-health-monitor-regional-status',
  ROLE_FAMILY_APPLICATION_ENABLED_COMPANIES = 'role_family_application_enabled_companies',
  TRABA_ACQUISITION_CONFIG = 'traba_acquisition_config',
  OPS_CONSOLE_UPDATE_PROMPT = 'ops_console_update_prompt',
  WORKER_OUTREACH_CONFIG = 'worker_outreach_config',
  KIOSK_ANNOUNCEMENTS_CONFIG = 'kiosk_announcements_config',
  ATTRIBUTE_V3_DASHBOARD = 'attribute_v3_dashboard',
  OPS_CONTROL_CENTER_CONFIG = 'ops_control_center_config',
  TERMINATION_AUTHORITY_EMAILS = 'termination_authority_emails',
  /** Company Facts (Pliny) config; see `CompanyFactsPlinyConfig`. */
  COMPANY_FACTS_PLINY = 'company_facts_pliny',
  /**
   * Neutron ops-action execution gates (`master` + per-action booleans).
   * Register `PUBLISH_SHIFT_DRAFT` when wiring shift draft publish (shift-drafts.ts).
   */
  NEUTRON_OPS_ACTIONS = 'neutron_ops_actions',
}

export type CompanyFactsPlinyRoleFactsSource =
  | 'role_discovery_facts'
  | 'attributes_v3'

export interface CompanyFactsPlinyLaunchGatesRequirementsConfig {
  roleFacts: {
    source: CompanyFactsPlinyRoleFactsSource
  }
}

/** Launch gates config on the `company_facts_pliny` dynamic config. */
export interface CompanyFactsPlinyLaunchGatesConfig {
  /** Show the launch readiness strip on the Company Facts tab. */
  isVisible: boolean
  requirements: CompanyFactsPlinyLaunchGatesRequirementsConfig
}

/**
 * Ops-console slice of the `company_facts_pliny` Statsig dynamic config.
 */
export interface CompanyFactsPlinyConfig {
  launchGates: CompanyFactsPlinyLaunchGatesConfig
}

/**
 * Shape of the `termination_authority_emails` Statsig dynamic config.
 * Allowlist of internal-user emails that may execute W2 terminations
 * directly from the Termination Action Center, bypassing the escalation
 * step when an open incident or claim exists.
 */
export interface TerminationAuthorityEmailsConfig {
  emails?: string[]
}

export type AppUpdateUrgency = 'recommended' | 'mandatory'

export interface AppUpdatePromptConfig {
  minBuildTimestamp: number
  urgency: AppUpdateUrgency
  message: string
  dismissCooldownMs: number
}

export interface OutreachSmsCostConfig {
  smsCostPerMessage: number
}

export interface GlobalWorkflowAccessConfig {
  allowedEmails?: string[]
}

export interface OperatorConfig {
  operatorMissesConfig?: {
    operatorId: string
  }
  autoFillConfig?: {
    operatorId: string
  }
}

export interface MinimumAgeRequirementsConfig {
  companyMinimumAges?: Record<string, number>
}

export interface FieldMonitorConfig {
  defaultPageSize?: number
}

export interface WorkerVettingConfig {
  langfuseTraceSearchUrl: string
  elevenLabsConvoAIUrl: string
  scoutAssessmentLangfuseDatasetName: string
  defaultPayRateAcceptableDiff: number
  twilioCallLogUrl: string
}

export interface DefaultVettingQuestion {
  id?: string
  title: string
  priority: string
  vettingEvalType: string
  question: string
  greatAnswer?: string
  goodAnswer: string
  badAnswer: string
  isEditable?: boolean
}

export interface DefaultVettingQuestionsConfig {
  defaultQuestions: DefaultVettingQuestion[]
}

export interface OverbookingRestrictionsConfig {
  // States where overbooking is restricted by default
  restrictedStates?: string[]
  // Company IDs that are allowed to use overbooking even in restricted states
  allowedCompanies?: string[]
  // Location IDs that are allowed to use overbooking even in restricted states
  allowedLocations?: string[]
}

export interface SentinelConfig {
  batchSize?: number
}

export interface ShiftCompletionPredictionConfig {
  greenThreshold: number // Completion rate >= this is green (default 0.9)
  yellowThreshold: number // Completion rate >= this is yellow (default 0.5), below is red
  batchSize?: number // Max batch size for bulk prediction requests (default 50)
  batchRequestDelay?: number // Delay between sequential batch requests (ms) to avoid overwhelming the server (default 150)
}

export interface SuggestedTimeBetweenShiftsConfig {
  suggestedTimeBetweenShiftsHours?: number
}

export interface LockedSearchPresetsConfig {
  lockedPresetIds?: string[]
}

export interface RegionHealthMonitorRegionalStatusConfig {
  eligibleCoverageRatioStableThreshold?: number
  eligibleCoverageRatioAtRiskThreshold?: number
  fillRateStableThreshold?: number
  fillRateAtRiskThreshold?: number
  surplusStableThreshold?: number
  surplusAtRiskThreshold?: number
  deficitStableThreshold?: number
  deficitAtRiskThreshold?: number
}
